import { useLayoutEffect, useState } from "react";

/**
 * Tracks an element's size so a layout can be recomputed from it.
 *
 * A callback ref rather than a `useRef`, because these screens render a loading
 * state first: an effect keyed on a ref object runs once while `current` is
 * still null and never again, leaving the layout stuck at its fallback size.
 * Keying on the node itself re-runs the moment the element appears.
 */
export function useSize(): { ref: (node: HTMLDivElement | null) => void; width: number; height: number } {
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    if (node === null) return;

    const measure = (width: number, height: number) => {
      setSize((current) =>
        current.width === width && current.height === height ? current : { width, height },
      );
    };

    // Measure straight away rather than waiting to be told. A hidden document
    // — a background tab, or a browser restoring a session — gets no
    // ResizeObserver callbacks at all, and a table that waited for one would
    // come up blank and stay blank until something resized.
    const rect = node.getBoundingClientRect();
    measure(rect.width, rect.height);

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry === undefined) return;
      measure(entry.contentRect.width, entry.contentRect.height);
    });
    observer.observe(node);

    // And re-measure on the way back, in case the layout moved while away.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const now = node.getBoundingClientRect();
      measure(now.width, now.height);
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      observer.disconnect();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [node]);

  return { ref: setNode, width: size.width, height: size.height };
}
